<template>
  <section>
    <FormSelect
      :config="{ options: options.conditions }"
      :customLabel="decamelize"
      label="Condition"
      placeholder="Set Condition"
      :value="value.condition"
      @input="setCondition"
    />
    <div
      v-if="shouldShow('courses')"
      class="pt-4 "
    >
      <div v-if="value.courses && value.courses.length">
        <h6 class="pl-2">Selected Courses</h6>
        <hr />
      </div>
      <AdvanceRequisiteCourseSelect
        v-if="shouldShow('courses')"
        class="px-4"
        :value="value.courses"
        @input="setValue('courses', $event)"
      />
      <hr />
    </div>
    <SchemaForm
      :value="value"
      :fields="schema"
      :columns="2"
      @input="setCombinedValues"
    />
    <div
      v-if="value.condition"
      class="row pb-4"
    >
      <AppButton
        :raised="false"
        class="mt-2"
        @click="showAdvancedInputs = !showAdvancedInputs"
      >
        <span class="">{{ showAdvancedInputs ? 'Hide': 'Show' }} Advanced Fields</span>
      </AppButton>
    </div>

    <div v-if="value.subRules && value.subRules.length">
      <div class="row">
        <div class="col-6">
          <h5>Sub Rules</h5>

        </div>
        <div class="col-6">
          <AppButton
            v-if="shouldShow('subRules')"
            @click="modals.addSubRules = true"
          >
            <AppIcon name="edit" /> <span class="ml-2">Edit Sub Rules</span>
          </AppButton>
        </div>
      </div>
      <AdvanceRequisiteRulesReadonly
        class="border rounded-5 mb-2 req"
        :value="value.subRules"
        :level="1"
      />
    </div>
    <div
      v-else
      class="row ml-0"
    >
      <AppButton
        v-if="shouldShow('subRules')"
        @click="modals.addSubRules = true"
      >
        <AppIcon
          class="ml-0 pl-0"
          name="add"
        /> <span class="ml-2">Add Sub Rules</span>
      </AppButton>
    </div>

    <AppModal
      v-if="modals.addSubRules"
      :stacked="true"
      :noBodyOverflow="false"
      @close="closeModal"
      @clickaway="closeModal"
    >
      <template #modal-header>
        <AppHeading>Sub Rules</AppHeading>
      </template>
      <template #modal-body>
        <AdvanceRequisiteRules
          :value="value.subRules"
          class="mt-2"
          @input="setValue('subRules', $event)"
        >
          Sub Rule
        </AdvanceRequisiteRules>
      </template>
      <template #modal-footer>
        <div class="d-flex mr-3">
          <AppButton @click="closeModal">Done</AppButton>
        </div>
      </template>
    </AppModal>
  </section>
</template>

<script>
import FormSelect from '@/components/Forms/FormSelect'
import FormText from '@/components/Forms/FormText'
import FormTextarea from '@/components/Forms/FormTextarea'
import FormNumber from '@/components/Forms/FormNumber'
import { decamelize, capitalize } from '@shared/utils/string'
import SchemaForm from '@/components/Forms/Form'
import AdvanceRequisiteCourseSelect from './AdvanceRequisiteCourseSelect'
import AdvanceRequisiteRulesReadonly from './AdvanceRequisiteRulesReadonly'
export default {
  name: 'AdvanceRequisiteRule',
  components: {
    FormSelect,
    SchemaForm,
    AdvanceRequisiteRules: () => import('./AdvanceRequisiteRules.vue'),
    AdvanceRequisiteCourseSelect,
    AdvanceRequisiteRulesReadonly,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    return {
      showAdvancedInputs: false,
      modals: {
        addSubRules: false,
      },
    }
  },
  computed: {
    schema () {
      return Object.keys(FIELD_CONDITION_MAP)
        .map(fieldKey => this.shouldShow(fieldKey) && FIELD_CONFIG[fieldKey])
        .filter(Boolean)
    },
    options () {
      return FIELD_OPTIONS
    },
  },
  created () {
    if (this.value.priority || this.value.tag) this.showAdvancedInputs = true
  },
  methods: {
    closeModal () {
      this.modals.addSubRules = false
    },
    shouldShow (fieldName) {
      const showField = FIELD_CONDITION_MAP[fieldName].includes(this.value.condition) || !FIELD_CONDITION_MAP[fieldName].length
      if (fieldName === 'tag') {
        return this.showAdvancedInputs && showField
      }
      if (fieldName === 'priority') {
        return this.showAdvancedInputs && showField
      } else return showField
    },
    setCondition (v) {
      this.$emit('input', { condition: v })
    },
    setValue (k, v) {
      this.$emit('input', { ...this.value, [k]: v })
    },
    setCombinedValues (values) {
      this.$emit('input', { ...this.value, ...values })
    },
  },
}

const FIELD_CONDITION_MAP = {
  courses: [
    'course',
    'transfer',
    'passFail',
    'residential',
    'spread',
    'perTerm',
    'perDiscipline',
    'gpa',
    'grade',
    'lastResidential',
  ],
  type: [
    'course',
    'transfer',
    'passFail',
    'residential',
    'perTerm',
    'perDiscipline',
    'lastResidential',
    'share',
  ],
  blocks: [
    'share',
    'credential',
  ],
  min: [
    'course',
    'transfer',
    'passFail',
    'residential',
    'perTerm',
    'perDiscipline',
    'lastResidential',
    'spread',
    'gpa',
    'grade',
    'subRules',
    'credential',
    'noncourse',
  ],
  max: [
    'course',
    'transfer',
    'passFail',
    'residential',
    'perTerm',
    'perDiscipline',
    'lastResidential',
    'spread',
    'subRules',
    'share',
  ],
  text: [
    'complete',
    'incomplete',
    'if',
    'elseIf',
    'share',
    'noncourse',
    'scribe',
    'freeformText',
  ],
  notes: [], // Empty array means it's shown for all conditions
  tag: [],
  priority: [
    'course',
  ],
  subRules: [
    'subRules',
    'complete',
    'incomplete',
    'if',
    'elseIf',
    'else',
    'course',
    'noncourse',
    'credential',
  ],
}

const FIELD_OPTIONS = {
  conditions: [
    'complete',
    'course',
    'credential',
    'else',
    'elseIf',
    'freeformText',
    'gpa',
    'grade',
    'if',
    'incomplete',
    'lastResidential',
    'noncourse',
    'passFail',
    'perDiscipline',
    'perTerm',
    'residential',
    'scribe',
    'share',
    'spread',
    'subRules',
    'transfer',
  ],
  type: [
    'classes',
    'credits',
  ],
  blocks: [
    'ALLBLOCKS',
    'COLLEGE',
    'CONC',
    'DEGREE',
    'LIBL',
    'MAJOR',
    'MINOR',
    'OTHER',
    'PROGRAM',
    'SCHOOL',
    'SPEC',
    'THISBLOCK',
  ],
}

const FIELD_CONFIG = {
  min: {
    name: 'min',
    type: FormNumber,
    placeholder: 'Set minimum',
    label: 'Minimum',
    descriptionLong: 'Minimum value for the current rule.',
  },
  max: {
    name: 'max',
    type: FormNumber,
    placeholder: 'Set maximum',
    label: 'Maximum',
    descriptionLong: 'Maximum value for the current rule.',
  },
  text: {
    name: 'text',
    type: FormTextarea,
    placeholder: 'Set text',
    label: 'Text',
    rows: 1,
  },
  notes: {
    name: 'notes',
    type: FormTextarea,
    placeholder: 'Enter notes',
    label: 'Notes',
    rows: 1,
  },
  type: {
    name: 'type',
    type: FormSelect,
    placeholder: 'Set type',
    label: 'Type',
    config: {
      options: FIELD_OPTIONS['type'],
    },
    descriptionLong: 'Select Classes to specify number of classes that must be taken. Otherwise select credits to specify number of credits',
    customLabel: decamelize,
  },
  blocks: {
    name: 'blocks',
    type: FormSelect,
    placeholder: 'Set block type',
    label: 'Block type',
    config: {
      options: FIELD_OPTIONS['blocks'],
    },
    descriptionLong: 'Type of block credentials that can be used to satisfy the rule.',
    customLabel: el => el && capitalize(el.toLowerCase() || ''),

  },
  tag: {
    name: 'tag',
    type: FormText,
    placeholder: 'Set tag',
    label: 'Tag',
    descriptionLong: 'Tags are custom inputs that may be set on any rule to give it a unique identifier.',
  },
  priority: {
    name: 'priority',
    type: FormNumber,
    placeholder: 'Set priority',
    label: 'Priority',
    descriptionLong: 'Priority of the current rule. 0 is neutral priority, positive numbers are high priority, negative numbers are low priority.',
    config: {
      min: undefined,
    },
  },
}
</script>
